import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Platform } from 'react-native';
import { usePreventRemove } from '@react-navigation/native';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { useMeetingConfirm } from './MeetingConfirm';

export type ExamLeaveGuardReport = {
  active: boolean;
  kind: 'group' | 'one_to_one';
  minSupervisors: number;
  /** Seconds with too few supervisors before every student's session ends (server-owned). */
  maxUnsupervisedSeconds: number;
  /** Other supervisors connected right now. */
  otherSupervisorNames: string[];
  /** Set when that clock is already running, so the dialog states what is left. */
  absenceDeadlineAt: string | null;
  stepBack: (() => Promise<void>) | null;
};

const inactiveReport: ExamLeaveGuardReport = {
  active: false,
  kind: 'group',
  minSupervisors: 1,
  maxUnsupervisedSeconds: 0,
  otherSupervisorNames: [],
  absenceDeadlineAt: null,
  stepBack: null,
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

function formatAbsenceBudget(t: Translate, seconds: number): string {
  // Round down: telling a lecturer they have less time than they do is safe.
  if (seconds >= 120) {
    const minutes = Math.floor(seconds / 60);
    return t('oralExam.supervisorLeave.durationMinutes', { defaultValue: '{{minutes}} minutes', minutes });
  }
  if (seconds >= 60) {
    return t('oralExam.supervisorLeave.durationOneMinute', { defaultValue: '1 minute' });
  }
  return t('oralExam.supervisorLeave.durationSeconds', { defaultValue: '{{seconds}} seconds', seconds });
}

type LeaveMessageFacts = Omit<ExamLeaveGuardReport, 'active' | 'stepBack'>;

export function supervisorLeaveMessage(
  t: Translate,
  report: LeaveMessageFacts,
  nowMs: number,
): string {
  if (report.kind === 'one_to_one') {
    return t('oralExam.supervisorLeave.messageOneToOne', {
      defaultValue: "The student's exam keeps running and you can rejoin from your calendar.",
    });
  }
  const { minSupervisors, otherSupervisorNames } = report;
  if (otherSupervisorNames.length >= minSupervisors) {
    return t('oralExam.supervisorLeave.messageGroupCovered', {
      defaultValue: "{{names}} will keep supervising, so the students' exams carry on. You can rejoin from your calendar or the invite link.",
      names: otherSupervisorNames.join(', '),
    });
  }
  const remainingSeconds = report.absenceDeadlineAt
    ? Math.max(0, Math.floor((Date.parse(report.absenceDeadlineAt) - nowMs) / 1000))
    : report.maxUnsupervisedSeconds;
  const duration = formatAbsenceBudget(t, remainingSeconds);
  if (minSupervisors <= 1) {
    return t('oralExam.supervisorLeave.messageGroupLast', {
      defaultValue: "You're the last supervisor. If no supervisor rejoins within {{duration}}, every student's exam ends and they must rebook. You can rejoin from your calendar or the invite link.",
      duration,
    });
  }
  return t('oralExam.supervisorLeave.messageGroupShort', {
    defaultValue: "This exam needs {{required}} supervisors. If there are still fewer than {{required}} in {{duration}}, every student's exam ends and they must rebook. You can rejoin from your calendar or the invite link.",
    required: minSupervisors,
    duration,
  });
}

type ExamLeaveGuardContextValue = {
  requestLeave: () => Promise<boolean>;
  setReport: (report: ExamLeaveGuardReport) => void;
  completeLeave: () => void;
};

const ExamLeaveGuardContext = createContext<ExamLeaveGuardContextValue | null>(null);

let registeredRequestLeave: (() => Promise<boolean>) | null = null;

/** Module-level hook for chrome outside the meeting screen (e.g. user menu logout). */
export function requestExamLeave(): Promise<boolean> {
  if (!registeredRequestLeave) {
    return Promise.resolve(true);
  }
  return registeredRequestLeave();
}

export function useExamLeaveGuardReporter(report: ExamLeaveGuardReport): void {
  const ctx = useContext(ExamLeaveGuardContext);

  const otherSupervisorNamesKey = report.otherSupervisorNames.join('\n');
  useLayoutEffect(() => {
    if (!ctx) return undefined;
    ctx.setReport({
      active: report.active,
      kind: report.kind,
      minSupervisors: report.minSupervisors,
      maxUnsupervisedSeconds: report.maxUnsupervisedSeconds,
      otherSupervisorNames: otherSupervisorNamesKey ? otherSupervisorNamesKey.split('\n') : [],
      absenceDeadlineAt: report.absenceDeadlineAt,
      stepBack: report.stepBack,
    });
    return undefined;
  }, [
    ctx,
    report.active,
    report.kind,
    report.minSupervisors,
    report.maxUnsupervisedSeconds,
    otherSupervisorNamesKey,
    report.absenceDeadlineAt,
    report.stepBack,
  ]);

  useLayoutEffect(() => {
    if (!ctx) return undefined;
    return () => {
      ctx.setReport(inactiveReport);
    };
  }, [ctx]);
}

export function useExamLeaveGuardRequestLeave(): {
  requestLeave: () => Promise<boolean>;
  completeLeave: () => void;
} {
  const ctx = useContext(ExamLeaveGuardContext);
  return {
    requestLeave: ctx?.requestLeave ?? requestExamLeave,
    completeLeave: ctx?.completeLeave ?? (() => {}),
  };
}

type ExamLeaveGuardProviderProps = {
  children: ReactNode;
  /** Called when leave is confirmed via in-app Back or browser history trap. */
  onLeaveConfirmed: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigation: NavigationProp<ParamListBase> | any;
};

/**
 * Running-exam leave guard: meeting confirm, browser history trap (web), tab close
 * prompt, and stack `usePreventRemove` share one `requestLeave` path.
 */
export function ExamLeaveGuardProvider({
  children,
  onLeaveConfirmed,
  navigation,
}: ExamLeaveGuardProviderProps) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const reportRef = useRef<ExamLeaveGuardReport>(inactiveReport);
  const [, bumpReport] = useState(0);
  const leaveConfirmedRef = useRef(false);
  const leavingRef = useRef(false);
  const aliveRef = useRef(true);
  const trapInstalledRef = useRef(false);

  const setReport = useCallback((next: ExamLeaveGuardReport) => {
    reportRef.current = next;
    if (!next.active && !leavingRef.current) {
      leaveConfirmedRef.current = false;
    }
    bumpReport((version) => version + 1);
  }, []);

  const report = reportRef.current;
  const preventRemove = report.active && !leaveConfirmedRef.current && !leavingRef.current;

  const requestLeave = useCallback(async (): Promise<boolean> => {
    if (leaveConfirmedRef.current || leavingRef.current) {
      return true;
    }
    const { current } = reportRef;
    const { active, stepBack } = current;
    if (!active) {
      return true;
    }

    const message = supervisorLeaveMessage(t, current, Date.now());
    return confirm({
      title: t('oralExam.supervisorLeave.title', { defaultValue: 'Leave the exam?' }),
      message,
      cancelLabel: t('oralExam.supervisorLeave.stay', { defaultValue: 'Stay' }),
      confirmLabel: t('oralExam.supervisorLeave.leaveExam', { defaultValue: 'Leave exam' }),
      destructive: true,
      overlayTestID: 'supervisor-leave-dialog',
      messageTestID: 'supervisor-leave-message',
      cancelTestID: 'supervisor-leave-stay',
      confirmTestID: 'supervisor-leave-confirm',
      onConfirm: stepBack
        ? async () => {
          leavingRef.current = true;
          leaveConfirmedRef.current = true;
          bumpReport((version) => version + 1);
          try {
            await stepBack();
            return true;
          } catch {
            leavingRef.current = false;
            leaveConfirmedRef.current = false;
            bumpReport((version) => version + 1);
            return false;
          }
        }
        : undefined,
      confirmErrorMessage: t('oralExam.supervisorLeave.stepBackError', {
        defaultValue: "Couldn't step back from the student. Check your connection, or leave anyway.",
      }),
      confirmAnywayLabel: t('oralExam.supervisorLeave.leaveAnyway', { defaultValue: 'Leave anyway' }),
      confirmErrorTestID: 'supervisor-leave-error',
      confirmAnywayTestID: 'supervisor-leave-anyway',
    });
  }, [confirm, t]);

  useEffect(() => {
    registeredRequestLeave = requestLeave;
    return () => {
      registeredRequestLeave = null;
    };
  }, [requestLeave]);

  const completeLeave = useCallback(() => {
    leavingRef.current = true;
    leaveConfirmedRef.current = true;
    bumpReport((version) => version + 1);
    setTimeout(() => {
      onLeaveConfirmed();
    }, 0);
  }, [onLeaveConfirmed]);

  usePreventRemove(preventRemove, ({ data }) => {
    requestLeave().then((ok) => {
      if (ok) {
        leaveConfirmedRef.current = true;
        bumpReport((version) => version + 1);
        setTimeout(() => {
          navigation.dispatch(data.action);
        }, 0);
      }
    }).catch(() => {});
  });

  useEffect(() => {
    const canTrapBrowserHistory = Platform.OS === 'web'
      && typeof window !== 'undefined'
      && typeof window.history?.pushState === 'function'
      && typeof window.addEventListener === 'function';
    if (!canTrapBrowserHistory || !preventRemove) {
      return undefined;
    }

    let ignorePop = false;
    const trapUrl = window.location.href;

    // A new onLeaveConfirmed (bookings arriving) re-runs this effect. Pushing
    // again would stack entries; popping here sends the lecturer to the calendar.
    if (!trapInstalledRef.current) {
      window.history.pushState({ examLeaveGuardTrap: true }, '', trapUrl);
      trapInstalledRef.current = true;
    }

    const onPopState = () => {
      if (ignorePop) {
        ignorePop = false;
        return;
      }
      if (leaveConfirmedRef.current || leavingRef.current) {
        return;
      }
      window.history.pushState({ examLeaveGuardTrap: true }, '', trapUrl);
      requestLeave().then((ok) => {
        if (ok) {
          completeLeave();
        }
      }).catch(() => {});
    };

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy browsers require returnValue; D3 leave-site prompt.
      const legacyEvent = event as BeforeUnloadEvent & { returnValue: string };
      legacyEvent.returnValue = '';
      return '';
    };

    window.addEventListener('popstate', onPopState);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Pop the trap only when this screen unmounts. Popping while it stays
      // mounted is a real browser Back: React Navigation follows it to the
      // calendar. That is what removed the "open in another window" screen
      // after a second tab kicked this one.
      if (
        !aliveRef.current
        && trapInstalledRef.current
        && !leaveConfirmedRef.current
        && !leavingRef.current
      ) {
        trapInstalledRef.current = false;
        ignorePop = true;
        window.history.back();
      }
    };
  }, [completeLeave, preventRemove, requestLeave, report.active]);

  // Layout cleanup runs before the history-trap effect's cleanup, so unmount
  // is visible when that cleanup decides whether to pop the trap.
  useLayoutEffect(() => () => {
    aliveRef.current = false;
  }, []);

  const value = useMemo(
    () => ({
      requestLeave,
      setReport,
      completeLeave,
    }),
    [completeLeave, requestLeave, setReport],
  );

  return (
    <ExamLeaveGuardContext.Provider value={value}>
      {children}
    </ExamLeaveGuardContext.Provider>
  );
}
